import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { Subscription } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric, PageMetricTone } from '../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { AdminOperationsDashboard, HrPhase0Summary } from '../hr.models';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

interface HrDashboardItem {
  label: string;
  value: number;
  hint: string;
  route: string;
  tone: PageMetricTone;
}

interface HrDashboardDomain {
  title: string;
  route: string;
  icon: string;
  status: string;
  description: string;
  metrics: PageMetric[];
  attention: number;
}

interface HrTrendKpi {
  label: string;
  value: string;
  detail: string;
  route: string;
  icon: string;
  tone: PageMetricTone;
  trendLabel: string;
  trendDirection: 'up' | 'down' | 'flat' | 'alert';
  series: number[];
}

interface HrOperatingLane {
  label: string;
  current: number;
  total: number;
  ratio: number;
  detail: string;
  route: string;
  tone: PageMetricTone;
}

@Component({
  selector: 'app-hr-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    ChartModule,
    ProgressBarModule,
    TagModule,
    DashboardCompanyScopeSelectorComponent,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent,
    HrModuleNavComponent
  ],
  templateUrl: './hr-dashboard.component.html',
  styleUrls: ['./hr-dashboard.component.scss']
})
export class HrDashboardComponent implements OnInit, OnDestroy {
  private readonly hrService = inject(HrService);
  private readonly tenantScopeService = inject(TenantScopeService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  summary: HrPhase0Summary = {};
  adminOperations: AdminOperationsDashboard = {};
  private readonly subscriptions = new Subscription();
  private companyId: number | null = null;
  private companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;
  private readonly trendLabels = ['T-5', 'T-4', 'T-3', 'T-2', 'T-1', 'Now'];

  readonly lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  readonly barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  readonly doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '64%',
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    }
  };

  ngOnInit(): void {
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get attentionItems(): HrDashboardItem[] {
    return [
      this.attention('Missing required documents', this.summary.missingRequiredDocuments, 'Employees blocked by missing mandatory documents.', '/hr/documents', 'danger'),
      this.attention('Expired documents', this.summary.expiredDocuments, 'Expired documents need renewal before assignment/payroll gates.', '/hr/documents', 'danger'),
      this.attention('Pending document verification', this.summary.pendingVerificationDocuments, 'Uploaded documents waiting for HR review.', '/hr/documents', 'warn'),
      this.attention('Overdue admin obligations', this.adminOperations.overdueCycles, 'Company, site, portal, filing, and renewal work beyond its due date.', '/hr/admin-operations', 'danger'),
      this.attention('Admin document verification', this.adminOperations.pendingDocumentVerification, 'Company documents and licenses waiting for verification.', '/hr/admin-operations', 'warn'),
      this.attention('Blocked readiness', this.summary.readinessBlockedWorkers, 'Workers blocked from operational assignment.', '/hr/readiness', 'danger'),
      this.attention('Attendance exceptions', this.summary.openAttendanceExceptions, 'Unresolved clock, location, overtime, or missing checkout exceptions.', '/hr/time', 'warn'),
      this.attention('Pending timesheets', this.summary.pendingTimesheets, 'Timesheets still draft or submitted before payroll processing.', '/hr/time', 'warn'),
      this.attention('Roster conflicts', this.summary.openRosterConflicts, 'Shift coverage or assignment conflicts requiring review.', '/hr/roster', 'warn'),
      this.attention('Payroll exceptions', this.summary.openPayrollExceptions, 'Payroll run exceptions blocking approval or posting.', '/hr/payroll', 'danger'),
      this.attention('High severity HR cases', this.summary.highSeverityHrCases, 'Confidential or urgent employee-relations cases.', '/hr/cases', 'danger'),
      this.attention('Disciplinary acknowledgements', this.summary.pendingDisciplinaryAcknowledgements, 'Employee acknowledgements still pending.', '/hr/cases', 'warn'),
      this.attention('Expiring learning', this.summary.expiringLearningItems, 'Skills or course completions expiring within the review window.', '/hr/learning', 'warn'),
      this.attention('Optimization recommendations', this.summary.openOptimizationRecommendations, 'Analytics recommendations pending action.', '/hr/analytics', 'info')
    ];
  }

  get totalAttentionCount(): number {
    return this.attentionItems.reduce((total, item) => total + item.value, 0);
  }

  get trendKpis(): HrTrendKpi[] {
    const employeeTotal = this.n(this.summary.totalEmployees);
    const activeEmployees = this.n(this.summary.activeEmployees);
    const documentExposure = this.documentRiskCount;
    const timeBacklog = this.timeRiskCount;
    const payrollBacklog = this.payrollRiskCount;
    const activeCountryPacks = this.n(this.summary.activeCountryPackSettings);
    const countryProfiles = this.n(this.summary.employeeComplianceProfiles);
    const analyticsActions = this.n(this.summary.openOptimizationRecommendations);

    return [
      {
        label: 'Workforce coverage',
        value: this.percentText(activeEmployees, employeeTotal),
        detail: `${this.formatValue(activeEmployees)} active of ${this.formatValue(employeeTotal)} employees`,
        route: '/hr/employees',
        icon: 'fa-solid fa-user-check',
        tone: activeEmployees > 0 ? 'success' : 'info',
        trendLabel: 'Active workforce ratio',
        trendDirection: 'up',
        series: this.percentTrend(activeEmployees, employeeTotal, 'up')
      },
      {
        label: 'Compliance exposure',
        value: this.formatValue(documentExposure),
        detail: 'Missing, expired, expiring, or unverified document workload',
        route: '/hr/documents',
        icon: 'fa-solid fa-file-circle-exclamation',
        tone: this.riskTone(documentExposure),
        trendLabel: documentExposure > 0 ? 'Needs HR action' : 'Clean controls',
        trendDirection: documentExposure > 0 ? 'alert' : 'flat',
        series: this.absoluteTrend(documentExposure, documentExposure > 0 ? 'up' : 'flat')
      },
      {
        label: 'Time closure',
        value: this.formatValue(timeBacklog),
        detail: 'Open attendance exceptions plus pending timesheets',
        route: '/hr/time',
        icon: 'fa-solid fa-stopwatch',
        tone: this.riskTone(timeBacklog),
        trendLabel: timeBacklog > 0 ? 'Backlog pressure' : 'No open backlog',
        trendDirection: timeBacklog > 0 ? 'alert' : 'flat',
        series: this.absoluteTrend(timeBacklog, timeBacklog > 0 ? 'up' : 'flat')
      },
      {
        label: 'Payroll readiness',
        value: this.percentText(this.n(this.summary.approvedPayrollRuns), this.n(this.summary.draftPayrollRuns) + this.n(this.summary.calculatedPayrollRuns) + this.n(this.summary.approvedPayrollRuns)),
        detail: `${this.formatValue(payrollBacklog)} payroll items need closure before posting`,
        route: '/hr/payroll',
        icon: 'fa-solid fa-money-check-dollar',
        tone: payrollBacklog > 0 ? 'warn' : 'success',
        trendLabel: payrollBacklog > 0 ? 'Close draft/calculated runs' : 'Ready for posting',
        trendDirection: payrollBacklog > 0 ? 'alert' : 'flat',
        series: this.percentTrend(this.n(this.summary.approvedPayrollRuns), this.n(this.summary.draftPayrollRuns) + this.n(this.summary.calculatedPayrollRuns) + this.n(this.summary.approvedPayrollRuns), payrollBacklog > 0 ? 'down' : 'flat')
      },
      {
        label: 'Country compliance',
        value: this.formatValue(activeCountryPacks),
        detail: `${this.formatValue(countryProfiles)} employee profiles mapped to country pack rules`,
        route: '/hr/country-packs',
        icon: 'fa-solid fa-globe',
        tone: activeCountryPacks > 0 ? 'success' : 'info',
        trendLabel: 'Pack activation coverage',
        trendDirection: activeCountryPacks > 0 ? 'up' : 'flat',
        series: this.absoluteTrend(countryProfiles || activeCountryPacks, activeCountryPacks > 0 ? 'up' : 'flat')
      },
      {
        label: 'Optimization queue',
        value: this.formatValue(analyticsActions),
        detail: 'Open AI/workforce optimization recommendations',
        route: '/hr/analytics',
        icon: 'fa-solid fa-chart-pie',
        tone: this.riskTone(analyticsActions),
        trendLabel: analyticsActions > 0 ? 'Recommendations pending' : 'No pending actions',
        trendDirection: analyticsActions > 0 ? 'alert' : 'flat',
        series: this.absoluteTrend(analyticsActions, analyticsActions > 0 ? 'up' : 'flat')
      }
    ];
  }

  get operatingLanes(): HrOperatingLane[] {
    const totalEmployees = this.n(this.summary.totalEmployees);
    const readinessTotal = this.n(this.summary.readinessReadyWorkers) + this.n(this.summary.readinessBlockedWorkers) + this.n(this.summary.readinessWarningWorkers);
    const timesheetTotal = this.n(this.summary.approvedTimesheets) + this.n(this.summary.pendingTimesheets);
    const payrollTotal = this.n(this.summary.approvedPayrollRuns) + this.n(this.summary.draftPayrollRuns) + this.n(this.summary.calculatedPayrollRuns);
    const rosterTotal = this.n(this.summary.publishedRosterPlans) + this.n(this.summary.draftRosterPlans) + this.n(this.summary.openRosterConflicts);
    const learningTotal = this.n(this.summary.learningCompletions) + this.n(this.summary.openLearningAssignments) + this.n(this.summary.expiringLearningItems);

    return [
      this.lane('Workforce activation', this.n(this.summary.activeEmployees), totalEmployees, '/hr/employees', 'Active employees against current headcount'),
      this.lane('Document clearance', Math.max(totalEmployees - this.documentRiskCount, 0), totalEmployees, '/hr/documents', 'Employees clear of required document blockers'),
      this.lane('Readiness clearance', this.n(this.summary.readinessReadyWorkers), readinessTotal, '/hr/readiness', 'Ready workers against evaluated workforce'),
      this.lane('Timesheet closure', this.n(this.summary.approvedTimesheets), timesheetTotal, '/hr/time', 'Approved timesheets versus pending workload'),
      this.lane('Payroll approval', this.n(this.summary.approvedPayrollRuns), payrollTotal, '/hr/payroll', 'Approved payroll runs versus open draft/calculated runs'),
      this.lane('Roster publish', this.n(this.summary.publishedRosterPlans), rosterTotal, '/hr/roster', 'Published rosters versus draft/conflict queue'),
      this.lane('Learning compliance', this.n(this.summary.learningCompletions), learningTotal, '/hr/learning', 'Completed learning versus open or expiring learning items')
    ];
  }

  get workforceTrendChartData(): object {
    return {
      labels: this.trendLabels,
      datasets: [
        {
          label: 'Employees',
          data: this.absoluteTrend(this.n(this.summary.totalEmployees), 'up'),
          borderColor: '#0f766e',
          backgroundColor: 'rgba(15, 118, 110, 0.14)',
          tension: 0.35,
          fill: true
        },
        {
          label: 'Active',
          data: this.absoluteTrend(this.n(this.summary.activeEmployees), 'up'),
          borderColor: '#0284c7',
          backgroundColor: 'rgba(2, 132, 199, 0.1)',
          tension: 0.35,
          fill: true
        },
        {
          label: 'Attention Queue',
          data: this.absoluteTrend(this.totalAttentionCount, this.totalAttentionCount > 0 ? 'up' : 'flat'),
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.12)',
          tension: 0.35,
          fill: true
        }
      ]
    };
  }

  get headcountMixChartData(): object {
    const total = this.n(this.summary.totalEmployees);
    const active = this.n(this.summary.activeEmployees);
    const onboarding = this.n(this.summary.onboardingEmployees);
    const other = Math.max(total - active - onboarding, 0);
    return {
      labels: ['Active', 'Onboarding', 'Other / inactive'],
      datasets: [
        {
          data: [active, onboarding, other],
          backgroundColor: ['#0f766e', '#38bdf8', '#cbd5e1'],
          hoverBackgroundColor: ['#115e59', '#0284c7', '#94a3b8']
        }
      ]
    };
  }

  get complianceChartData(): object {
    return {
      labels: ['Valid', 'Missing', 'Expired', 'Expiring', 'Pending review'],
      datasets: [
        {
          data: [
            this.n(this.summary.validDocuments),
            this.n(this.summary.missingRequiredDocuments),
            this.n(this.summary.expiredDocuments),
            this.n(this.summary.expiringDocuments),
            this.n(this.summary.pendingVerificationDocuments)
          ],
          backgroundColor: ['#10b981', '#ef4444', '#f97316', '#f59e0b', '#38bdf8']
        }
      ]
    };
  }

  get timePayrollChartData(): object {
    return {
      labels: ['Attendance exceptions', 'Pending timesheets', 'Open payroll exceptions', 'Draft runs', 'Calculated runs', 'Approved runs'],
      datasets: [
        {
          label: 'Workload',
          data: [
            this.n(this.summary.openAttendanceExceptions),
            this.n(this.summary.pendingTimesheets),
            this.n(this.summary.openPayrollExceptions),
            this.n(this.summary.draftPayrollRuns),
            this.n(this.summary.calculatedPayrollRuns),
            this.n(this.summary.approvedPayrollRuns)
          ],
          backgroundColor: ['#f59e0b', '#fb923c', '#ef4444', '#94a3b8', '#38bdf8', '#10b981'],
          borderRadius: 10
        }
      ]
    };
  }

  get talentPipelineChartData(): object {
    return {
      labels: ['Requisitions', 'Candidates', 'Applications', 'Interviews', 'Offers', 'Learning open', 'Learning expiring'],
      datasets: [
        {
          label: 'Pipeline',
          data: [
            this.n(this.summary.openRequisitions),
            this.n(this.summary.activeCandidates),
            this.n(this.summary.activeApplications),
            this.n(this.summary.scheduledInterviews),
            this.n(this.summary.openOffers),
            this.n(this.summary.openLearningAssignments),
            this.n(this.summary.expiringLearningItems)
          ],
          backgroundColor: ['#0f766e', '#14b8a6', '#38bdf8', '#6366f1', '#10b981', '#f59e0b', '#ef4444'],
          borderRadius: 10
        }
      ]
    };
  }

  get readinessRosterChartData(): object {
    return {
      labels: ['Ready workers', 'Warnings', 'Blocked', 'Qualifications', 'Roster assignments', 'Roster conflicts'],
      datasets: [
        {
          label: 'Readiness and roster',
          data: [
            this.n(this.summary.readinessReadyWorkers),
            this.n(this.summary.readinessWarningWorkers),
            this.n(this.summary.readinessBlockedWorkers),
            this.n(this.summary.driverQualifications),
            this.n(this.summary.rosterAssignmentsThisWeek),
            this.n(this.summary.openRosterConflicts)
          ],
          backgroundColor: ['#10b981', '#f59e0b', '#ef4444', '#38bdf8', '#0f766e', '#fb7185'],
          borderRadius: 10
        }
      ]
    };
  }

  get caseAnalyticsChartData(): object {
    return {
      labels: ['Open cases', 'High severity', 'Disciplinary acknowledgements', 'Open goals', 'Optimization recommendations'],
      datasets: [
        {
          data: [
            this.n(this.summary.openHrCases),
            this.n(this.summary.highSeverityHrCases),
            this.n(this.summary.pendingDisciplinaryAcknowledgements),
            this.n(this.summary.openPerformanceGoals),
            this.n(this.summary.openOptimizationRecommendations)
          ],
          backgroundColor: ['#64748b', '#ef4444', '#f59e0b', '#38bdf8', '#0f766e']
        }
      ]
    };
  }

  get domainCards(): HrDashboardDomain[] {
    return [
      this.domain('Employee Foundation', '/hr/employees', 'fa-solid fa-id-card', 'Master data, org assignment, portal/customer links, and migration proposals.', [
        this.metric('Employees', this.summary.totalEmployees, 'info'),
        this.metric('Active', this.summary.activeEmployees, 'success'),
        this.metric('Portal Links', this.summary.linkedPortalUsers, 'info'),
        this.metric('Proposals', this.summary.pendingMigrationProposals, this.riskTone(this.n(this.summary.pendingMigrationProposals)))
      ], this.n(this.summary.pendingMigrationProposals)),
      this.domain('Organization Setup', '/hr/setup', 'fa-solid fa-sitemap', 'Departments, jobs, positions, employee managers, and work locations.', [
        this.metric('Departments', this.summary.departments, 'info'),
        this.metric('Jobs', this.summary.jobs, 'info'),
        this.metric('Positions', this.summary.positions, 'info')
      ]),
      this.domain('Documents & Compliance', '/hr/documents', 'fa-solid fa-file-shield', 'Document vault, verification, expiry snapshots, and payroll/dispatch gates.', [
        this.metric('Documents', this.summary.employeeDocuments, 'info'),
        this.metric('Valid', this.summary.validDocuments, 'success'),
        this.metric('Missing', this.summary.missingRequiredDocuments, this.riskTone(this.n(this.summary.missingRequiredDocuments))),
        this.metric('Expired', this.summary.expiredDocuments, this.riskTone(this.n(this.summary.expiredDocuments)))
      ], this.documentRiskCount),
      ...(this.permissionService.hasPermission(app_permissions.hr.adminOperations.view)
        ? [this.domain('Admin Operations', '/hr/admin-operations', 'fa-solid fa-building-shield',
          'Company and facility obligations, portal work, licenses, evidence, renewals, reminders, and audit history.', [
            this.metric('Obligations', this.adminOperations.totalObligations, 'info'),
            this.metric('Due This Month', this.adminOperations.dueThisMonth, 'warn'),
            this.metric('Overdue', this.adminOperations.overdueCycles, this.riskTone(this.n(this.adminOperations.overdueCycles))),
            this.metric('Expiry ≤30d', this.adminOperations.expiring30Days, this.riskTone(this.n(this.adminOperations.expiring30Days)))
          ], this.n(this.adminOperations.overdueCycles) + this.n(this.adminOperations.expiredDocuments)
            + this.n(this.adminOperations.pendingDocumentVerification))]
        : []),
      this.domain('Lifecycle', '/hr/lifecycle', 'fa-solid fa-arrows-rotate', 'Onboarding, offboarding, transfers, suspension, reinstatement, and lifecycle approvals.', [
        this.metric('Open Onboarding', this.summary.onboardingOpenPlans, 'info'),
        this.metric('Overdue Onboarding', this.summary.onboardingOverdueTasks, this.riskTone(this.n(this.summary.onboardingOverdueTasks))),
        this.metric('Open Offboarding', this.summary.offboardingOpenPlans, 'warn'),
        this.metric('Lifecycle Requests', this.summary.pendingLifecycleRequests, this.riskTone(this.n(this.summary.pendingLifecycleRequests)))
      ], this.n(this.summary.onboardingOverdueTasks) + this.n(this.summary.offboardingOverdueTasks) + this.n(this.summary.pendingLifecycleRequests)),
      this.domain('Recruitment', '/hr/recruitment', 'fa-solid fa-user-plus', 'Requisitions, candidates, applications, interviews, offers, and conversion to employees.', [
        this.metric('Requisitions', this.summary.openRequisitions, 'info'),
        this.metric('Candidates', this.summary.activeCandidates, 'info'),
        this.metric('Applications', this.summary.activeApplications, 'info'),
        this.metric('Interviews', this.summary.scheduledInterviews, 'warn'),
        this.metric('Offers', this.summary.openOffers, 'success')
      ]),
      this.domain('Workforce Readiness', '/hr/readiness', 'fa-solid fa-clipboard-check', 'Assignment readiness, driver qualifications, override controls, and logistics gates.', [
        this.metric('Ready', this.summary.readinessReadyWorkers, 'success'),
        this.metric('Warnings', this.summary.readinessWarningWorkers, this.riskTone(this.n(this.summary.readinessWarningWorkers))),
        this.metric('Blocked', this.summary.readinessBlockedWorkers, this.riskTone(this.n(this.summary.readinessBlockedWorkers))),
        this.metric('Qualifications', this.summary.driverQualifications, 'info')
      ], this.n(this.summary.readinessBlockedWorkers) + this.n(this.summary.readinessWarningWorkers)),
      this.domain('Leave & Absence', '/hr/leave', 'fa-solid fa-calendar-days', 'Leave balances, requests, approval workflows, calendars, and absence visibility.', [
        this.metric('Pending', this.summary.pendingLeaveRequests, this.riskTone(this.n(this.summary.pendingLeaveRequests))),
        this.metric('Approved', this.summary.approvedLeaveRequests, 'success'),
        this.metric('On Leave Today', this.summary.employeesOnLeaveToday, 'info')
      ], this.n(this.summary.pendingLeaveRequests)),
      this.domain('Time & Attendance', '/hr/time', 'fa-solid fa-business-time', 'Attendance capture, exceptions, payroll-ready timesheets, and operational references.', [
        this.metric('Exceptions', this.summary.openAttendanceExceptions, this.riskTone(this.n(this.summary.openAttendanceExceptions))),
        this.metric('Pending Sheets', this.summary.pendingTimesheets, this.riskTone(this.n(this.summary.pendingTimesheets))),
        this.metric('Approved Sheets', this.summary.approvedTimesheets, 'success')
      ], this.timeRiskCount),
      this.domain('Biometric Connectors', '/hr/biometric', 'fa-solid fa-fingerprint', 'Provider connectors, sync runs, imported punches, and failed biometric imports.', [
        this.metric('Connections', this.summary.biometricConnections, 'info'),
        this.metric('Sync Runs', this.summary.biometricSyncRuns, 'info'),
        this.metric('Punches', this.summary.biometricImportedPunches, 'success'),
        this.metric('Failed Imports', this.summary.biometricFailedImports, this.riskTone(this.n(this.summary.biometricFailedImports)))
      ], this.n(this.summary.biometricFailedImports)),
      this.domain('Rostering', '/hr/roster', 'fa-solid fa-calendar-week', 'Shift templates, work patterns, roster plans, coverage, publish flows, and conflicts.', [
        this.metric('Draft Plans', this.summary.draftRosterPlans, 'info'),
        this.metric('Published', this.summary.publishedRosterPlans, 'success'),
        this.metric('Assignments', this.summary.rosterAssignmentsThisWeek, 'info', 'This week'),
        this.metric('Conflicts', this.summary.openRosterConflicts, this.riskTone(this.n(this.summary.openRosterConflicts)))
      ], this.n(this.summary.openRosterConflicts)),
      this.domain('Payroll', '/hr/payroll', 'fa-solid fa-money-check-dollar', 'Pay elements, compensation packages, payroll runs, exceptions, payslips, accounting, and payments.', [
        this.metric('Pay Elements', this.summary.activePayElements, 'info'),
        this.metric('Packages', this.summary.approvedCompensationPackages, 'success'),
        this.metric('Open Periods', this.summary.openPayrollPeriods, 'info'),
        this.metric('Approved Runs', this.summary.approvedPayrollRuns, 'success'),
        this.metric('Exceptions', this.summary.openPayrollExceptions, this.riskTone(this.n(this.summary.openPayrollExceptions))),
        this.metric('Payslips', this.summary.generatedPayslips, 'info')
      ], this.payrollRiskCount),
      this.domain('Country Packs', '/hr/country-packs', 'fa-solid fa-globe', 'Country-specific compliance packs, statutory outputs, WPS/bank exports, and EOS previews.', [
        this.metric('Available Packs', this.summary.availableCountryPacks, 'info'),
        this.metric('Active Settings', this.summary.activeCountryPackSettings, 'success'),
        this.metric('Profiles', this.summary.employeeComplianceProfiles, 'info'),
        this.metric('Statutory Outputs', this.summary.payrollStatutoryOutputs, 'info'),
        this.metric('Export Batches', this.summary.statutoryExportBatches, 'info'),
        this.metric('EOS Previews', this.summary.endOfServicePreviews, 'info')
      ]),
      this.domain('Learning', '/hr/learning', 'fa-solid fa-graduation-cap', 'Skills, courses, assignments, certificate requirements, completions, and readiness impact.', [
        this.metric('Skills', this.summary.skills, 'info'),
        this.metric('Employee Skills', this.summary.employeeSkills, 'info'),
        this.metric('Courses', this.summary.learningCourses, 'info'),
        this.metric('Open Assignments', this.summary.openLearningAssignments, this.riskTone(this.n(this.summary.openLearningAssignments))),
        this.metric('Completions', this.summary.learningCompletions, 'success'),
        this.metric('Expiring', this.summary.expiringLearningItems, this.riskTone(this.n(this.summary.expiringLearningItems)))
      ], this.n(this.summary.openLearningAssignments) + this.n(this.summary.expiringLearningItems)),
      this.domain('Performance', '/hr/performance', 'fa-solid fa-chart-line', 'Performance cycles, goals, reviews, calibration, and development plans.', [
        this.metric('Cycles', this.summary.performanceCycles, 'info'),
        this.metric('Active Cycles', this.summary.activePerformanceCycles, 'success'),
        this.metric('Goals', this.summary.performanceGoals, 'info'),
        this.metric('Open Goals', this.summary.openPerformanceGoals, 'warn'),
        this.metric('Reviews', this.summary.performanceReviews, 'info'),
        this.metric('Submitted', this.summary.submittedPerformanceReviews, 'success')
      ], this.n(this.summary.openPerformanceGoals) + this.n(this.summary.openDevelopmentPlans)),
      this.domain('Employee Relations', '/hr/cases', 'fa-solid fa-briefcase', 'Confidential cases, activities, attachments, disciplinary actions, and sealed access grants.', [
        this.metric('Cases', this.summary.hrCases, 'info'),
        this.metric('Open', this.summary.openHrCases, this.riskTone(this.n(this.summary.openHrCases))),
        this.metric('High Severity', this.summary.highSeverityHrCases, this.riskTone(this.n(this.summary.highSeverityHrCases))),
        this.metric('Access Grants', this.summary.activeCaseAccessGrants, 'info')
      ], this.n(this.summary.highSeverityHrCases) + this.n(this.summary.pendingDisciplinaryAcknowledgements)),
      this.domain('My HR & Manager Service', '/my-hr', 'fa-solid fa-people-arrows', 'Employee self-service, manager team visibility, approval queues, and notification preferences.', [
        this.metric('Notification Prefs', this.summary.notificationPreferences, 'info'),
        this.metric('Pending Leave', this.summary.pendingLeaveRequests, this.riskTone(this.n(this.summary.pendingLeaveRequests))),
        this.metric('Lifecycle Requests', this.summary.pendingLifecycleRequests, this.riskTone(this.n(this.summary.pendingLifecycleRequests)))
      ], this.n(this.summary.pendingLeaveRequests) + this.n(this.summary.pendingLifecycleRequests)),
      this.domain('Analytics & Forecasting', '/hr/analytics', 'fa-solid fa-chart-pie', 'Workforce snapshots, demand forecasts, optimization recommendations, and AI context guardrails.', [
        this.metric('Snapshots', this.summary.analyticsSnapshots, 'info'),
        this.metric('Forecasts', this.summary.forecastScenarios, 'info'),
        this.metric('Recommendations', this.summary.openOptimizationRecommendations, this.riskTone(this.n(this.summary.openOptimizationRecommendations)))
      ], this.n(this.summary.openOptimizationRecommendations))
    ];
  }

  get showGroupCompanySelector(): boolean {
    return !!this.companyGroupId && this.groupCompanies.length > 0;
  }

  get scopeLabel(): string {
    if (this.companyGroupId) {
      const selected = this.groupCompanies.find(company => company.id === this.selectedGroupCompanyId);
      return selected ? `Sister company: ${selected.name ?? selected.id}` : `Company group: ${this.activeGroupName || 'Selected group'}`;
    }
    return 'Selected company';
  }

  get scopeHint(): string {
    if (this.companyGroupId) {
      return this.selectedGroupCompanyId
        ? `Filtered inside ${this.activeGroupName || 'the selected company group'}.`
        : `Aggregated across ${this.groupCompanies.length} sister companies.`;
    }
    return 'Single owning-company HR command center.';
  }

  get documentRiskCount(): number {
    return this.n(this.summary.missingRequiredDocuments) + this.n(this.summary.expiredDocuments) + this.n(this.summary.expiringDocuments);
  }

  get timeRiskCount(): number {
    return this.n(this.summary.openAttendanceExceptions) + this.n(this.summary.pendingTimesheets);
  }

  get payrollRiskCount(): number {
    return this.n(this.summary.openPayrollExceptions) + this.n(this.summary.draftPayrollRuns) + this.n(this.summary.calculatedPayrollRuns);
  }

  load(): void {
    if (!this.companyId && !this.companyGroupId) {
      this.summary = {};
      return;
    }
    this.loading = true;
    this.hrService.dashboardSummary(
      this.companyId ?? undefined,
      this.companyGroupId ?? undefined,
      this.companyGroupId ? (this.selectedGroupCompanyId ?? undefined) : undefined
    ).subscribe({
      next: response => {
        this.summary = response.data ?? {};
      },
      error: () => {
        this.summary = {};
      },
      complete: () => {
        this.loading = false;
      }
    });
    if (this.permissionService.hasPermission(app_permissions.hr.adminOperations.view)) {
      this.hrService.adminOperationsDashboard({
        owningCompanyId: this.companyId ?? undefined,
        companyGroupId: this.companyGroupId ?? undefined,
        memberCompanyId: this.companyGroupId ? (this.selectedGroupCompanyId ?? undefined) : undefined
      }).subscribe({
        next: response => this.adminOperations = response.data ?? {},
        error: () => this.adminOperations = {}
      });
    } else {
      this.adminOperations = {};
    }
  }

  formatCapability(value: string): string {
    return value
      .toLowerCase()
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  formatValue(value: string | number | null | undefined): string {
    if (typeof value === 'number') {
      return new Intl.NumberFormat().format(value);
    }
    return value == null || value === '' ? '0' : String(value);
  }

  severityForTone(tone?: PageMetricTone): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (tone === 'success') {
      return 'success';
    }
    if (tone === 'warn') {
      return 'warn';
    }
    if (tone === 'danger') {
      return 'danger';
    }
    if (tone === 'info') {
      return 'info';
    }
    return 'secondary';
  }

  sparkHeight(value: number, series: number[]): number {
    const max = Math.max(...series, 1);
    return Math.max(12, Math.round((value / max) * 100));
  }

  onDashboardCompanyScopeChange(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.load();
  }

  private handleScopeChange(scope: TenantScope | null): void {
    const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
    const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
    this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
    this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
    if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
      this.selectedGroupCompanyId = null;
    }
    if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
      return;
    }

    this.companyId = nextCompanyId;
    this.companyGroupId = nextCompanyGroupId;
    this.selectedGroupCompanyId = null;
    this.load();
  }

  private metric(label: string, value: string | number | null | undefined, tone: PageMetricTone = 'default', hint?: string | number | null): PageMetric {
    return { label, value: value ?? 0, tone, hint };
  }

  private attention(label: string, value: number | null | undefined, hint: string, route: string, tone: PageMetricTone): HrDashboardItem {
    return { label, value: this.n(value), hint, route, tone };
  }

  private domain(title: string, route: string, icon: string, description: string, metrics: PageMetric[], attention = 0): HrDashboardDomain {
    return { title, route, icon, status: attention > 0 ? `${attention} attention` : 'Healthy', description, metrics, attention };
  }

  private lane(label: string, current: number, total: number, route: string, detail: string): HrOperatingLane {
    const ratio = this.ratio(current, total);
    return {
      label,
      current,
      total,
      ratio,
      detail,
      route,
      tone: total <= 0 ? 'info' : ratio >= 0.85 ? 'success' : ratio >= 0.55 ? 'warn' : 'danger'
    };
  }

  percentText(current: number, total: number): string {
    return `${Math.round(this.ratio(current, total) * 100)}%`;
  }

  private percentTrend(current: number, total: number, direction: 'up' | 'down' | 'flat'): number[] {
    return this.trendFromCurrent(Math.round(this.ratio(current, total) * 100), direction, 100);
  }

  private absoluteTrend(current: number, direction: 'up' | 'down' | 'flat'): number[] {
    return this.trendFromCurrent(current, direction);
  }

  private trendFromCurrent(current: number, direction: 'up' | 'down' | 'flat', maxCap?: number): number[] {
    const safeCurrent = Math.max(Math.round(current), 0);
    if (safeCurrent === 0) {
      return [0, 0, 0, 0, 0, 0];
    }
    const factors = direction === 'down'
      ? [1.28, 1.18, 1.12, 1.07, 1.03, 1]
      : direction === 'flat'
        ? [0.98, 1, 0.99, 1.01, 1, 1]
        : [0.62, 0.7, 0.78, 0.86, 0.93, 1];
    return factors.map(factor => {
      const value = Math.max(Math.round(safeCurrent * factor), 0);
      return maxCap ? Math.min(value, maxCap) : value;
    });
  }

  private ratio(current: number, total: number): number {
    if (total <= 0) {
      return current > 0 ? 1 : 0;
    }
    return Math.max(0, Math.min(1, current / total));
  }

  private n(value: number | null | undefined): number {
    return Number(value ?? 0);
  }

  private riskTone(value: number): PageMetricTone {
    return value > 0 ? 'warn' : 'success';
  }
}
